/** Workspace settings, and the parts the admin console shares with it (K4). */
export const settingsCopy = {
  retry: 'Retry',
  loadFailed: {
    workspace: 'Could not load this workspace.',
    members: 'Could not load members.',
    invites: 'Could not load invites.',
    keys: 'Could not load API keys.',
    providers: 'Could not load providers.',
    spendCap: 'Could not load the monthly cap.',
    agents: 'Could not load agents.',
    usage: 'Could not load usage.',
  },
  refreshFailed: {
    members: 'Could not refresh members.',
    invites: 'Could not refresh invites.',
    keys: 'Could not refresh API keys.',
    providers: 'Could not refresh providers.',
    agents: 'Could not refresh agents.',
    usage: 'Could not refresh usage.',
    spendCap: 'Could not refresh the monthly cap.',
  },
  page: {
    title: 'Workspace settings',
    crumbFallback: 'Workspace',
    roleLine: (workspace: string, role: string) =>
      `${workspace} · your role: ${role}`,
    sections: 'Workspace settings sections',
    tabs: {
      members: 'Members',
      agents: 'Agents',
      keys: 'Keys',
      usage: 'Usage',
      providers: 'Providers',
    },
    rename: 'Rename',
    nameLabel: 'Workspace name',
    renameFailed: 'Failed to rename workspace — try again.',
    newProvider: 'New provider',
    notFound: 'Workspace not found, or you are not a member.',
    goHome: 'Go home',
    dangerZone: 'Danger zone',
  },
  workspaceDelete: {
    action: 'Delete workspace',
    title: 'Delete workspace',
    description:
      'Permanently deletes the workspace, its topics and all messages.',
    describeNamed: (workspace: string) =>
      `Permanently deletes "${workspace}", its topics and all messages.`,
    confirm: 'Delete',
    committed: 'Workspace deletion started',
    pending: 'Workspace deletion is pending.',
    pendingHelp: 'It stays visible until cleanup finishes.',
    retryNow: 'Retry now',
    deleted: 'Workspace deleted',
    failed: 'Failed to delete workspace — try again.',
    /** The server's refusals by status; anything else is `failed`. */
    failure: (status?: number) =>
      status === 403
        ? 'Failed to delete workspace — only its owner can delete it.'
        : status === 409
          ? 'Failed to delete workspace — the system manages it.'
          : 'Failed to delete workspace — try again.',
  },
  members: {
    heading: 'Members',
    listLabel: 'Members',
    roleFor: (username: string) => `Role for ${username}`,
    remove: 'Remove',
    transfer: 'Transfer ownership',
    removeDialog: {
      title: 'Remove member',
      description: (username: string) =>
        `"${username}" loses access to this workspace.`,
      confirm: 'Remove',
    },
    transferDialog: {
      title: 'Transfer ownership',
      description: (username: string) =>
        `"${username}" becomes the owner, and your role changes to admin.`,
      confirm: 'Transfer',
    },
    add: 'Add member',
    addField: 'Username to add',
    addRole: 'Role for new member',
    addSubmit: 'Add',
    cancel: 'Cancel',
    cancelAdd: 'Cancel adding member',
    addFailed: 'Failed to add member — try again.',
    addNotFound: 'Failed to add member — check the username and try again.',
    removeFailed: 'Failed to remove member — try again.',
    removeOwnFailed:
      'Failed to remove member — ask another owner or admin to remove you.',
    roleFailed: 'Failed to update role — try again.',
    roleOwnFailed:
      'Failed to update role — ask another owner or admin to change yours.',
    transferFailed: 'Failed to transfer ownership — try again.',
    userModel: 'User model',
    memberModels: 'Member models',
  },
  invites: {
    heading: 'Invites',
    listLabel: 'Invites',
    expired: '(expired)',
    revoke: 'Revoke',
    open: 'Invite member',
    field: 'Email to invite',
    role: 'Role for invite',
    submit: 'Send invite',
    cancel: 'Cancel invite',
    sent: 'Invite sent',
    failed: 'Failed to send invite — check the address and try again.',
    revoked: 'Invite revoked',
    revokeFailed: 'Failed to revoke invite — try again.',
  },
  invitesEmpty: 'No pending invites.',
  agentActions: (name: string) => `More actions for ${name}`,
  agents: {
    heading: 'Agents',
    listLabel: 'Agents',
    empty: 'No agents in this workspace.',
    new: 'New agent',
    edit: 'Edit',
    pause: 'Pause',
    restore: 'Restore',
    delete: 'Delete',
    retryNow: 'Retry now',
    running: 'running',
    pending: {
      pause: 'pausing',
      restore: 'restoring',
      delete: 'deleting',
    },
    updatePending: (name: string) => `${name} updated`,
    deleteDialog: {
      title: 'Delete agent',
      description: (name: string) =>
        `Removes "${name}" from its topics. It can be restored later.`,
      confirm: 'Delete',
    },
    deleteFailed: 'Failed to delete agent — try again.',
    statusFailed: 'Failed to update agent status — try again.',
    /** A pause, restore or delete the server refused, by status. */
    lifecycleFailure: (
      kind: 'pause' | 'restore' | 'delete',
      status?: number,
    ) =>
      status === 404
        ? 'Agent no longer exists — refresh the list.'
        : status === 403
          ? 'Failed to update agent — ask a workspace admin for access.'
          : status === 409
            ? 'Failed to update agent — the workspace is being deleted.'
            : kind === 'delete'
              ? 'Failed to delete agent — try again.'
              : 'Failed to update agent status — try again.',
  },
  newKey: 'Add key',
  keys: {
    heading: 'Gateway keys',
    description: 'API keys for external tools.',
    created: 'This key is shown only once — copy it now.',
    copy: 'Copy',
    copied: 'Copied',
    dismiss: 'Dismiss',
    empty: 'No API keys yet.',
    listLabel: 'API keys',
    lastUsed: (date: string) => `last used ${date}`,
    neverUsed: 'never used',
    model: (model: string) => `model: ${model}`,
    scopedToTopic: 'scoped to topic',
    revoke: 'Revoke',
    name: 'Key name',
    topicScope: 'Topic scope',
    topicScopeHelp: 'Limits the key to this topic.',
    allTopics: 'All topics',
    optional: '(optional)',
    create: 'Create key',
    add: 'Add key',
    cancel: 'Cancel',
    showOptions: 'Show options',
    hideOptions: 'Hide options',
    modelOverride: 'Model override',
    revokeDialog: {
      title: 'Revoke API key',
      description: (name: string) =>
        `Tools that use "${name}" stop working. This cannot be undone.`,
      confirm: 'Revoke',
    },
    createFailed: 'Failed to create API key — try again.',
    revoked: 'Gateway key revoked',
    revokeFailed: 'Failed to revoke API key — try again.',
  },
  usage: {
    heading: 'Usage',
    caption: 'Token and cost usage by agent',
    model: 'Model',
    monthlyCap: 'Monthly cap',
    capValue: (spent: string, cap: string) => `${spent} spent of ${cap} cap`,
    noCap: (spent: string) => `${spent} spent · no cap`,
    capInput: 'Monthly spend cap in USD',
    capPlaceholder: 'USD',
    set: 'Set',
    clear: 'Clear',
    capReached: 'Monthly cap reached — raise or clear the cap.',
    capFailed: 'Failed to update the monthly cap — try again.',
    empty: 'No usage recorded yet.',
    noWorkspaces: 'No workspaces found.',
    columns: {
      agent: 'Agent',
      input: 'Input',
      output: 'Output',
      cached: 'Cached',
      total: 'Total',
      cost: 'Est. cost',
    },
    total: 'Total',
  },
  providers: {
    heading: 'Providers',
    add: 'Add provider',
    listLabel: 'Providers',
    workspaceDescription: 'Model provider credentials for this workspace',
    workspaceEmptyManager: 'No providers in this workspace yet.',
    workspaceEmptyMember: 'Ask an admin to add a provider to this workspace.',
    name: 'Name',
    type: 'Type',
    apiKey: 'API key',
    baseUrl: 'Base URL',
    required: '(required)',
    create: 'Create',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    keepKey: 'Keep key',
    keepUrl: 'Keep URL',
    keySet: 'key set',
    noKey: 'no key',
    keepKeyPlaceholder: 'Leave blank to keep the current key',
    clearKey: 'Clear key',
    keyCleared: 'Key will be cleared on save.',
    clearUrl: 'Clear URL',
    urlCleared: 'Base URL will be cleared on save.',
    baseUrlExample: (url: string) => `For example, ${url}`,
    baseUrlDefault: (url: string) => `Defaults to ${url}`,
    baseUrlOptional: 'Optional',
    typeHelp: {
      gemini: 'Vertex AI keys do not work here.',
      openai: 'Any OpenAI-compatible API.',
      glm: 'Coding Plan keys do not work here.',
    } as Record<string, string>,
    deleteDialog: {
      title: 'Delete provider',
      description: (name: string) =>
        `Agents that use "${name}" fail until they get another provider. This cannot be undone.`,
      confirm: 'Delete',
    },
    createFailed: 'Failed to create provider — try again.',
    updateFailed: 'Failed to update provider — try again.',
    deleteFailed: 'Failed to delete provider — try again.',
  },
} as const
